// Everything that goes wrong in the editor, in one place.
//
// Reports came in as "it is buggy in a lot of places and it freezes", which is
// impossible to act on: the errors were in each person's own console, and
// nobody has the console open. This collects them — JS errors, rejected
// promises, failed API calls, and the editor's own reports — keeps the last
// few, survives a reload, and can be copied out in one click.
//
// It only records. It never blocks anything, and a failure in here must never
// become a second problem: every entry point is wrapped.

const STORAGE_KEY = "prody-editor-problems";
const MAX_ENTRIES = 120;
// Beyond this the same message is counted, not stored again: one broken block
// re-rendering in a loop used to be a hundred identical lines.
const DEDUPE_WINDOW = 60 * 1000;

const state = {
  entries: [],
  listeners: new Set(),
};

function load() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) {
      state.entries = parsed.slice(-MAX_ENTRIES);
    }
  } catch (e) {
    state.entries = [];
  }
}

function persist() {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.entries));
  } catch (e) {
    // Blocked or full storage: the list still lives for this session.
  }
}

function notify() {
  for (const listener of state.listeners) {
    try {
      listener(state.entries);
    } catch (e) {
      // A broken listener must not stop the others.
    }
  }
}

function shortText(value, max = 400) {
  if (value === null || value === undefined) {
    return "";
  }
  const text = typeof value === "string" ? value : safeStringify(value);
  return text.length > max ? text.slice(0, max) + "…" : text;
}

function safeStringify(value) {
  try {
    if (value instanceof Error) {
      return `${value.name}: ${value.message}`;
    }
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
}

/**
 * Record one problem.
 * kind:   "js" | "promise" | "api" | "editor"
 * title:  one line, what went wrong
 * detail: anything that helps — server message, stack, file name
 */
// Two things the editor does on purpose look like errors from the outside:
//  - twig re-renders a template until its includes have arrived, so the first
//    passes always throw. loadTwig/loadSection report the real give-up.
//  - leaving a page aborts its in-flight requests as "Failed to fetch".
// Logging either would bury the problems that matter.
function isExpectedNoise(kind, title, detail) {
  const text = `${title} ${detail}`;
  // In a production build the stack names the bundle, not the module, so
  // matching on "twig.js" only worked in development. Twig's own promise
  // chain (Thenable.resolvedThen) is in every one of these stacks and is the
  // reliable marker.
  if (
    (kind === "js" || kind === "promise") &&
    (/twig\.js/.test(text) || /Thenable\.resolvedThen/.test(text))
  ) {
    return true;
  }
  if (kind === "api" && leaving && /Failed to fetch/.test(text)) {
    return true;
  }
  return false;
}

let leaving = false;

export function recordProblem(kind, title, detail = "", extra = {}) {
  try {
    if (isExpectedNoise(kind, title, detail)) {
      return;
    }
    const now = Date.now();
    const text = shortText(title, 200);
    const last = state.entries[state.entries.length - 1];
    // The same thing, again, straight away: count it instead of listing it.
    const recent = state.entries.find(
      (entry) =>
        entry.title === text &&
        entry.kind === kind &&
        now - entry.lastAt < DEDUPE_WINDOW
    );
    if (recent) {
      recent.count += 1;
      recent.lastAt = now;
      if (recent === last) {
        persist();
        notify();
        return;
      }
    } else {
      state.entries.push({
        kind,
        title: text,
        detail: shortText(detail),
        where: shortText(
          window.location.pathname + window.location.search,
          120
        ),
        at: now,
        lastAt: now,
        count: 1,
        ...extra,
      });
      if (state.entries.length > MAX_ENTRIES) {
        state.entries = state.entries.slice(-MAX_ENTRIES);
      }
    }
    persist();
    notify();
  } catch (e) {
    // Recording a problem must never raise one.
  }
}

export function getProblems() {
  return state.entries;
}

export function clearProblems() {
  state.entries = [];
  persist();
  notify();
}

export function onProblems(listener) {
  state.listeners.add(listener);
  return () => state.listeners.delete(listener);
}

// One plain-text block that can be pasted into a message. Times are local,
// because that is how people describe what they were doing.
export function problemsAsText() {
  const lines = [
    `Prody Block — problem log (${state.entries.length} entries)`,
    `${window.location.origin} · ${new Date().toLocaleString()}`,
    "",
  ];
  for (const entry of state.entries) {
    const when = new Date(entry.at).toLocaleTimeString();
    const times = entry.count > 1 ? ` ×${entry.count}` : "";
    lines.push(`[${when}] ${entry.kind.toUpperCase()}${times} ${entry.title}`);
    if (entry.where) {
      lines.push(`    page: ${entry.where}`);
    }
    if (entry.detail) {
      lines.push(`    ${entry.detail}`);
    }
  }
  return lines.join("\n");
}

// Catches everything the app does not catch itself. Called once from main.js.
export function installProblemLog(app) {
  load();

  window.addEventListener("beforeunload", () => {
    leaving = true;
  });

  window.addEventListener("error", (event) => {
    // Failed <img>/<script> loads arrive here too, without an Error object.
    if (event?.error instanceof Error) {
      recordProblem(
        "js",
        event.error.message || "Script error",
        event.error.stack ?? `${event.filename}:${event.lineno}`
      );
    } else if (event?.target?.src) {
      recordProblem("js", "A file failed to load", String(event.target.src));
    } else if (event?.message) {
      recordProblem(
        "js",
        event.message,
        `${event.filename ?? ""}:${event.lineno ?? ""}`
      );
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    recordProblem(
      "promise",
      reason?.message ?? shortText(reason, 200) ?? "A background task failed",
      reason?.stack ?? safeStringify(reason)
    );
  });

  if (app) {
    const previous = app.config.errorHandler;
    app.config.errorHandler = (err, instance, info) => {
      recordProblem(
        "js",
        err?.message ?? "Component error",
        `${info}\n${err?.stack ?? ""}`
      );
      if (typeof previous === "function") {
        previous(err, instance, info);
      } else {
        console.error(err);
      }
    };
  }
}
